/**
 * DCS World default key bindings.
 *
 * The whole point of this trainer is that what you learn here transfers. So
 * the keys are not invented — they are the stock DCS bindings, verified
 * against the published command lists rather than remembered:
 *
 *   G / LShift+G / LCtrl+G     gear toggle, down, up
 *   F / LShift+F / LCtrl+F     flaps toggle, down, up
 *   B / LShift+B / LCtrl+B     airbrake toggle, out, in
 *   LCtrl+H                    arresting hook
 *   W                          wheel brakes (held)
 *   PageUp / PageDown          throttle (held)
 *   \                          communications menu
 *
 * MODIFIERS ARE MATCHED EXACTLY, and that is the part worth being careful
 * about. LCtrl+H is the hook; bare H is nothing. If the match were "the H key
 * was pressed and we do not much mind what else", then holding Ctrl to do
 * something else and brushing H would drop the hook, and — far worse for a
 * trainer — pressing plain H would be accepted as "you lowered the hook" when
 * in DCS it would have done nothing at all. Teaching a keystroke that does not
 * work in the simulator you are training for is the one unforgivable bug in
 * this file.
 *
 * Left and right modifiers are accepted interchangeably. DCS distinguishes
 * them and its own binds use the left-hand ones, which is what the labels say;
 * refusing RCtrl would be pedantry that only ever annoys people.
 */

const MOD_CODES = new Set([
  'ControlLeft',
  'ControlRight',
  'ShiftLeft',
  'ShiftRight',
  'AltLeft',
  'AltRight',
  // Meta is tracked for one reason: without it, Cmd+G on a Mac resolves to
  // the bare G bind and drops the landing gear while the browser opens Find
  // Next. Modifiers are matched exactly, so an untracked one silently widens
  // every binding it touches.
  'MetaLeft',
  'MetaRight',
])

/** Fold left/right into one role, so RCtrl works where LCtrl is bound. */
export function modRole(code) {
  if (code === 'ControlLeft' || code === 'ControlRight') return 'ctrl'
  if (code === 'ShiftLeft' || code === 'ShiftRight') return 'shift'
  if (code === 'AltLeft' || code === 'AltRight') return 'alt'
  if (code === 'MetaLeft' || code === 'MetaRight') return 'meta'
  return null
}

export const isModifier = (code) => MOD_CODES.has(code)

/**
 * @typedef {object} Bind
 * @property {string} code      KeyboardEvent.code, so it is layout-independent
 * @property {string[]} mods    required modifier roles, exactly
 * @property {string} label     how DCS itself writes it
 * @property {boolean} [held]   true for press-and-hold commands
 */

/** @type {Record<string, Bind>} */
export const BINDS = {
  gearToggle: { code: 'KeyG', mods: [], label: 'G' },
  gearDown: { code: 'KeyG', mods: ['shift'], label: 'LShift + G' },
  gearUp: { code: 'KeyG', mods: ['ctrl'], label: 'LCtrl + G' },

  flapsToggle: { code: 'KeyF', mods: [], label: 'F' },
  flapsDown: { code: 'KeyF', mods: ['shift'], label: 'LShift + F' },
  flapsUp: { code: 'KeyF', mods: ['ctrl'], label: 'LCtrl + F' },

  brakeToggle: { code: 'KeyB', mods: [], label: 'B' },
  brakeOut: { code: 'KeyB', mods: ['shift'], label: 'LShift + B' },
  brakeIn: { code: 'KeyB', mods: ['ctrl'], label: 'LCtrl + B' },

  hook: { code: 'KeyH', mods: ['ctrl'], label: 'LCtrl + H' },

  wheelBrakes: { code: 'KeyW', mods: [], label: 'W', held: true },
  throttleUp: { code: 'PageUp', mods: [], label: 'Page Up', held: true },
  throttleDown: { code: 'PageDown', mods: [], label: 'Page Down', held: true },

  // `code` is the US physical position, which is layout-independent for
  // letters and emphatically not for punctuation. On a UK or ISO keyboard the
  // key printed \| sits left of Z and reports IntlBackslash; Backslash is the
  // #~ key by Enter. Without the alternate, a British user follows the on-
  // screen instruction, presses the key with a backslash printed on it, and
  // the ball call never registers — which holds the pattern at the ramp
  // permanently, because the ball call is a gate.
  comms: { code: 'Backslash', altCodes: ['IntlBackslash'], mods: [], label: '\\' },

  // ---- commands DCS ships UNBOUND ------------------------------------
  //
  // Direct lift control, the approach power compensator and the SAS switches
  // are all in the NAVAIR pattern and none of them has a stock keyboard
  // binding in DCS — they live on the HOTAS and on cockpit switches. Leaving
  // them out would mean teaching a CASE 1 with DLC missing, which on an F-14
  // approach is not a detail; pretending they are standard keys would be
  // worse. So they are bound here and flagged, and the key list says plainly
  // that these three will not work in DCS until you map them yourself.
  dlc: { code: 'KeyD', mods: [], label: 'D', noDcsDefault: true },
  autoThrottle: { code: 'KeyA', mods: [], label: 'A', noDcsDefault: true },
  sas: { code: 'KeyS', mods: [], label: 'S', noDcsDefault: true },

  // Trainer controls. Not DCS commands — this simulator's own, kept on keys
  // DCS does not use for anything a pattern needs, so nothing shadows a bind
  // you are meant to be learning.
  view1: { code: 'Digit1', mods: [], label: '1' },
  view2: { code: 'Digit2', mods: [], label: '2' },
  view3: { code: 'Digit3', mods: [], label: '3' },
  view4: { code: 'Digit4', mods: [], label: '4' },
  view5: { code: 'Digit5', mods: [], label: '5' },
  view6: { code: 'Digit6', mods: [], label: '6' },
  restart: { code: 'KeyR', mods: [], label: 'R' },
  startGroove: { code: 'KeyC', mods: [], label: 'C' },
  pause: { code: 'KeyP', mods: [], label: 'P' },
  // Chromium treats F1 as reserved and opens its own help centre no matter
  // what preventDefault says, so there is a second way in.
  help: { code: 'F1', altCodes: ['Slash'], mods: [], label: 'F1 or /' },
  recentre: { code: 'Numpad5', mods: [], label: 'Num 5' },
  // Seat height, live. Every downloaded cockpit sits at a slightly different
  // height and no amount of measuring beats being able to look at it.
  seatUp: { code: 'Numpad8', mods: [], label: 'Num 8' },
  seatDown: { code: 'Numpad2', mods: [], label: 'Num 2' },
}

const sameSet = (a, b) => a.length === b.length && a.every((m) => b.includes(m))

/**
 * Which bind, if any, does this key plus this exact set of held modifiers mean?
 *
 * Pure, so the awkward cases can be driven directly in a test: bare H must
 * resolve to nothing, LCtrl+H must resolve to the hook, and LCtrl+G must not
 * resolve to the plain gear toggle.
 *
 * @param {string} code       KeyboardEvent.code of the key just pressed
 * @param {string[]} heldMods modifier roles currently down, e.g. ['ctrl']
 * @returns {string|null} bind id
 */
export function resolveBind(code, heldMods = []) {
  for (const [id, bind] of Object.entries(BINDS)) {
    const codeMatches = bind.code === code || !!bind.altCodes?.includes(code)
    if (codeMatches && sameSet(bind.mods, heldMods)) return id
  }
  return null
}

/** Every bind that uses this physical key, for the "wrong modifier" hint. */
export function bindsForKey(code) {
  return Object.entries(BINDS)
    .filter(([, b]) => b.code === code || b.altCodes?.includes(code))
    .map(([id, b]) => ({ id, ...b }))
}

/**
 * Is this key plausibly someone trying to fly the aeroplane?
 *
 * Used to decide whether an unrecognised keypress is worth counting against
 * the pilot. Tab, Escape, the function keys and the arrows are how people use
 * a browser, not how they fly, and counting an alt-tab as a fumble makes the
 * debrief's "wrong keys" number a lie.
 */
export function looksLikeACommand(code) {
  return (
    /^Key[A-Z]$/.test(code) ||
    /^Digit\d$/.test(code) ||
    /^Numpad/.test(code) ||
    ['PageUp', 'PageDown', 'Backslash', 'IntlBackslash', 'Comma', 'Period', 'Slash',
     'Semicolon', 'Quote', 'BracketLeft', 'BracketRight', 'Minus', 'Equal',
     'Backquote', 'Space'].includes(code)
  )
}

/**
 * Listens to the document and turns key events into bind ids.
 *
 * Modifier state is tracked from keydown/keyup rather than read off
 * `event.ctrlKey`, because the event flags cannot tell left from right and
 * because a modifier released while the window was unfocused otherwise sticks
 * down forever. A blur listener clears everything for the same reason.
 */
export class DcsKeymap {
  /**
   * @param {object} handlers
   * @param {(id: string, bind: Bind) => void} [handlers.onPress]
   * @param {(id: string, bind: Bind) => void} [handlers.onRelease]
   * @param {(code: string, heldMods: string[]) => void} [handlers.onUnbound]
   */
  constructor(handlers = {}) {
    this.handlers = handlers
    this._heldMods = new Set()
    this._heldBinds = new Set()
    this._codeToBind = new Map()

    this._onKeyDown = (e) => this.keyDown(e.code, e.repeat, e)
    this._onKeyUp = (e) => this.keyUp(e.code)
    this._onBlur = () => this.clear()
  }

  attach(target = window) {
    target.addEventListener('keydown', this._onKeyDown)
    target.addEventListener('keyup', this._onKeyUp)
    target.addEventListener('blur', this._onBlur)
    this._target = target
    return this
  }

  detach() {
    this._target?.removeEventListener('keydown', this._onKeyDown)
    this._target?.removeEventListener('keyup', this._onKeyUp)
    this._target?.removeEventListener('blur', this._onBlur)
  }

  get heldMods() {
    return [...this._heldMods]
  }

  /** Bind ids currently held down, so a reset can re-arm them. */
  get heldBinds() {
    return [...this._heldBinds]
  }

  isHeld(bindId) {
    return this._heldBinds.has(bindId)
  }

  keyDown(code, repeat = false, event = null) {
    if (isModifier(code)) {
      this._heldMods.add(modRole(code))
      return null
    }
    const id = resolveBind(code, this.heldMods)

    // Page Up, Page Down and backslash all mean something to a browser. Once a
    // key is a recognised command it stops being the browser's — and this has
    // to happen on auto-repeat too, not just the first press, or holding Page
    // Down to bring the throttles back scrolls the document from the second
    // repeat onwards.
    if (id) event?.preventDefault?.()

    if (repeat) return null

    if (!id) {
      this.handlers.onUnbound?.(code, this.heldMods)
      return null
    }

    this._codeToBind.set(code, id)
    this._heldBinds.add(id)
    this.handlers.onPress?.(id, BINDS[id])
    return id
  }

  keyUp(code) {
    if (isModifier(code)) {
      this._heldMods.delete(modRole(code))
      // Releasing Ctrl while H is still down should end the hook command, not
      // leave it latched.
      return null
    }
    const id = this._codeToBind.get(code)
    if (!id) return null
    this._codeToBind.delete(code)
    this._heldBinds.delete(id)
    this.handlers.onRelease?.(id, BINDS[id])
    return id
  }

  clear() {
    for (const id of this._heldBinds) this.handlers.onRelease?.(id, BINDS[id])
    this._heldMods.clear()
    this._heldBinds.clear()
    this._codeToBind.clear()
  }
}
